import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export interface ISalvage {
  id?: string;
  leadId: string;
  vrm?: string | null;
  make?: string | null;
  model?: string | null;
  customerName?: string | null;
  bookedCollectionBy?: string | null;
  bookingDate?: string | null;
  copartInstructed: boolean;
  copartCollectedOn?: string | null;
  daysInStock?: number | null;
  collectionCost?: number | null;
  capValue?: number | null;
  proquoteValue?: number | null;
  projectedAuctionValue?: number | null;
  auctionRun1?: number | null;
  auctionRun2?: number | null;
  auctionRun3?: number | null;
  auctionRun4?: number | null;
  actualAuctionValue?: number | null;
  soldOn?: string | null;
  finalSellingFees?: number | null;
  vatCharges?: number | null;
  returnedToGp: boolean;
  returnedOn?: string | null;
  redeliveryCost?: number | null;
  vehiclePrice?: number | null;
  profit?: number | null;
}

export type SalvageStage = "awaitingCollection" | "awaitingAuction" | "sold" | "notSold" | "returned";

export interface ISalvageUserProfit {
  bookedCollectionBy?: string | null;
  sold: number;
  totalProfit: number;
  averageProfit: number;
}

export interface ISalvageSummary {
  awaitingCollection: number;
  awaitingAuction: number;
  sold: number;
  returned: number;
  averageDaysInStock?: number | null;
  averageProfit?: number | null;
  stockValueAwaitingSale: number;
  awaitingAuctionCost: number;
  notYetSoldCost: number;
  projectedRevenue: number;
  soldRevenue: number;
  soldProfit: number;
  returnedRedeliveryCost: number;
  profitByUser: ISalvageUserProfit[];
}

export interface ISetSalvageRequest {
  copartInstructed: boolean;
  copartCollectedOn: string | null;
  collectionCost: number | null;
  capValue: number | null;
  proquoteValue: number | null;
  projectedAuctionValue: number | null;
  auctionRun1: number | null;
  auctionRun2: number | null;
  auctionRun3: number | null;
  auctionRun4: number | null;
  actualAuctionValue: number | null;
  soldOn: string | null;
  finalSellingFees: number | null;
  vatCharges: number | null;
  returnedToGp: boolean;
  redeliveryCost: number | null;
}

export default function useSalvage(customerId?: string, leadId?: string, componentName = "useSalvage") {
  const { get, put } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const salvage = useQuery({
    queryKey: ["salvage", customerId, leadId],
    queryFn: () => get<DefaultResponseWithData<ISalvage>>(`/sav/customer/${customerId}/salvage/lead/${leadId}`),
    enabled: !!customerId && !!leadId,
  });

  const save = useMutation(
    (body: ISetSalvageRequest) => {
      if (!customerId || !leadId) throw new Error("Missing SAV customer or lead");
      return put<ISetSalvageRequest, DefaultResponseWithData<ISalvage>>(
        `/sav/customer/${customerId}/salvage/lead/${leadId}`,
        body
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["salvage", customerId, leadId]);
        queryClient.invalidateQueries(["salvage-summary", customerId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  return { salvage, save };
}

export function useSaveSalvage(customerId?: string, componentName = "useSaveSalvage") {
  const { put } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  return useMutation(
    ({ leadId, body }: { leadId: string; body: ISetSalvageRequest }) => {
      if (!customerId) throw new Error("Missing SAV customer");
      return put<ISetSalvageRequest, DefaultResponseWithData<ISalvage>>(
        `/sav/customer/${customerId}/salvage/lead/${leadId}`,
        body
      );
    },
    {
      onSuccess: (res, vars) => {
        const updated = res.data;
        queryClient.setQueriesData(
          {
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              query.queryKey[0] === "pagedTable" &&
              String(query.queryKey[1] ?? "").includes("/salvage"),
          },
          (old: DefaultResponseWithData<{ rows?: ISalvage[] }> | undefined) => {
            if (!old?.data?.rows || !updated) return old;
            return {
              ...old,
              data: {
                ...old.data,
                rows: old.data.rows.map((row) => (row.leadId === vars.leadId ? { ...row, ...updated } : row)),
              },
            };
          }
        );
        queryClient.invalidateQueries(["salvage-summary", customerId]);
        queryClient.invalidateQueries(["salvage", customerId, vars.leadId]);
      },
    }
  );
}

export function useSalvageSummary(customerId?: string, componentName = "useSalvageSummary") {
  const { get } = useApiHelper(apiConfig(componentName));

  const summary = useQuery({
    queryKey: ["salvage-summary", customerId],
    queryFn: () => get<DefaultResponseWithData<ISalvageSummary>>(`/sav/customer/${customerId}/salvage/summary`),
    enabled: !!customerId,
  });

  return { summary };
}
